import React, { useState } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  Button,
  Text,
  Badge,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  DismissRegular,
  WrenchRegular,
  ChevronDownRegular,
  ChevronRightRegular,
} from '@fluentui/react-icons';
import { ToolCallContent } from '../models/types';
import { ChunkCard } from './ChunkCard';

const useStyles = makeStyles({
  dialogSurface: {
    maxWidth: '900px',
    width: '90vw',
    maxHeight: '85vh',
  },
  dialogBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalM,
    overflowY: 'auto',
    overflowX: 'hidden',
    maxHeight: '60vh',
  },
  toolSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  toolHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3,
    },
  },
  toolHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  toolIcon: {
    color: tokens.colorBrandForeground1,
  },
  toolName: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  chunkCount: {
    marginLeft: tokens.spacingHorizontalS,
  },
  chunksContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalL,
  },
  emptyState: {
    padding: tokens.spacingVerticalXL,
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  titleIcon: {
    fontSize: '24px',
    color: tokens.colorBrandForeground1,
  },
});

interface ToolContentModalProps {
  isOpen: boolean;
  onClose: () => void;
  toolContent: ToolCallContent[];
}

export const ToolContentModal: React.FC<ToolContentModalProps> = ({
  isOpen,
  onClose,
  toolContent,
}) => {
  const classes = useStyles();
  const [expandedTools, setExpandedTools] = useState<Set<string>>(new Set(
    toolContent.length === 1 ? [toolContent[0].tool_name] : []
  ));

  const toggleTool = (toolName: string) => {
    const newExpanded = new Set(expandedTools);
    if (newExpanded.has(toolName)) {
      newExpanded.delete(toolName);
    } else {
      newExpanded.add(toolName);
    }
    setExpandedTools(newExpanded);
  };

  const totalChunks = toolContent.reduce((sum, tool) => sum + tool.chunks.length, 0);

  return (
    <Dialog open={isOpen} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface className={classes.dialogSurface}>
        <DialogTitle>
          <div className={classes.titleContainer}>
            <WrenchRegular className={classes.titleIcon} />
            <span>Tool Content Details</span>
            <Badge appearance="filled" color="brand" size="small">
              {totalChunks} {totalChunks === 1 ? 'chunk' : 'chunks'} from {toolContent.length} {toolContent.length === 1 ? 'tool' : 'tools'}
            </Badge>
          </div>
        </DialogTitle>

        <DialogBody className={classes.dialogBody}>
          {toolContent.length === 0 ? (
            <div className={classes.emptyState}>
              <Text>No tool content available</Text>
            </div>
          ) : (
            toolContent.map((tool) => (
              <div key={tool.tool_name} className={classes.toolSection}>
                <div
                  className={classes.toolHeader}
                  onClick={() => toggleTool(tool.tool_name)}
                >
                  <div className={classes.toolHeaderLeft}>
                    {expandedTools.has(tool.tool_name) ? (
                      <ChevronDownRegular />
                    ) : (
                      <ChevronRightRegular />
                    )}
                    <WrenchRegular className={classes.toolIcon} />
                    <Text className={classes.toolName}>{tool.display_name}</Text>
                    <Badge appearance="outline" size="small" className={classes.chunkCount}>
                      {tool.chunks.length} {tool.chunks.length === 1 ? 'chunk' : 'chunks'}
                    </Badge>
                  </div>
                </div>

                {expandedTools.has(tool.tool_name) && (
                  <div className={classes.chunksContainer}>
                    {tool.chunks.map((chunk) => (
                      <ChunkCard
                        key={chunk.id}
                        chunk={chunk}
                        toolName={tool.tool_name}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </DialogBody>

        <DialogActions>
          <Button appearance="secondary" onClick={onClose} icon={<DismissRegular />}>
            Close
          </Button>
        </DialogActions>
      </DialogSurface>
    </Dialog>
  );
};
